const currencyFormatter = new Intl.NumberFormat('pl-PL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  // Polish grouping normally starts at five digits, which would put "6103,97"
  // directly under "26 539,00" in the same column. Always group instead.
  useGrouping: true,
});

const compactFormatter = new Intl.NumberFormat('pl-PL', {
  maximumFractionDigits: 0,
  useGrouping: true,
});

const quantityFormatter = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 3 });

const dateFormatter = new Intl.DateTimeFormat('pl-PL', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const shortDateFormatter = new Intl.DateTimeFormat('pl-PL', {
  day: '2-digit',
  month: '2-digit',
});

const timeFormatter = new Intl.DateTimeFormat('pl-PL', {
  hour: '2-digit',
  minute: '2-digit',
});

const monthShortFormatter = new Intl.DateTimeFormat('pl-PL', { month: 'short' });

const monthLongFormatter = new Intl.DateTimeFormat('pl-PL', { month: 'long', year: 'numeric' });

/**
 * Amounts always carry their unit.
 *
 * Half the screens used to append " zł" by hand and half did not, so the same
 * figure read differently depending on where it appeared.
 */
export function formatMoney(amount: number): string {
  return `${currencyFormatter.format(amount)} zł`;
}

/** Bare number, for a table column whose header already states the unit. */
export function formatAmount(amount: number): string {
  return currencyFormatter.format(amount);
}

/** Whole złoty, for dashboard tiles where the grosz add noise. */
export function formatMoneyCompact(amount: number): string {
  return `${compactFormatter.format(amount)} zł`;
}

/** Ordered quantities: 2,5 m³ of concrete and 10 szt of anything else. */
export function formatQuantity(quantity: number): string {
  return quantityFormatter.format(quantity);
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return dateFormatter.format(new Date(value));
}

export function formatShortDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  return shortDateFormatter.format(new Date(value));
}

/** `YYYY-MM` → the first of that month, read as a local date. */
function monthDate(month: string): Date {
  const [year, index] = month.split('-').map(Number);
  return new Date(year ?? 1970, (index ?? 1) - 1, 1);
}

/** "sie" — an axis label, where the panel title already gives the year. */
export function formatMonthShort(month: string): string {
  return monthShortFormatter.format(monthDate(month));
}

/** "Sierpień 2026" — for tooltips and headings, where it stands alone. */
export function formatMonthLong(month: string): string {
  const label = monthLongFormatter.format(monthDate(month));
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** "dziś 09:41" / "wczoraj" / "16.08" — for the activity feed. */
export function formatRelativeDay(value: string | Date): string {
  const date = new Date(value);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (isSameDay(date, today)) return timeFormatter.format(date);
  if (isSameDay(date, yesterday)) return 'wczoraj';
  return shortDateFormatter.format(date);
}

/**
 * Polish has three plural forms: 1, 2–4, and everything else. `forms` is given
 * as [jedna, dwie, pięć] — e.g. ['faktura', 'faktury', 'faktur'].
 */
export function plural(count: number, forms: [string, string, string]): string {
  if (count === 1) return forms[0];

  const lastDigit = count % 10;
  const lastTwo = count % 100;
  const isFew = lastDigit >= 2 && lastDigit <= 4 && (lastTwo < 12 || lastTwo > 14);

  return isFew ? forms[1] : forms[2];
}

export function formatDayCount(days: number): string {
  const absolute = Math.abs(days);
  if (absolute === 1) return '1 dzień';
  return `${absolute} dni`;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
